import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { ArrowLeft, TrendingUp, TrendingDown, Users, CheckCircle2, Clock, AlertTriangle, BarChart3, Award, Target } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const Dashboard180 = () => {
  const navigate = useNavigate();
  const { cicloId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [ciclo, setCiclo] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, [cicloId]);

  const fetchDashboard = async () => {
    try {
      const [dashRes, cicloRes] = await Promise.all([
        axios.get(`${API}/api/feedback-180/dashboard/ciclo/${cicloId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/api/feedback-180/ciclos/${cicloId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
      ]);
      
      setDashboard(dashRes.data);
      setCiclo(cicloRes.data);
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!dashboard || !ciclo) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-gray-600">Dados não disponíveis</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Button variant="outline" onClick={() => navigate('/feedback/cultural')} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Voltar
      </Button>

      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard Analítico</h1>
          <p className="text-gray-600 mt-1">{ciclo.nome}</p>
        </div>

        {/* Métricas Gerais */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Autoavaliações</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {dashboard.percentual_concluido_auto}%
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {dashboard.total_autoavaliacoes} concluídas
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <CheckCircle2 className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full transition-all"
                    style={{ width: `${dashboard.percentual_concluido_auto}%` }}
                  ></div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Aval. Gestor</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {dashboard.percentual_concluido_gestor}%
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {dashboard.total_avaliacoes_gestor} concluídas
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div className="mt-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${dashboard.percentual_concluido_gestor}%` }}
                  ></div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Taxa Engajamento</p>
                  <p className="text-2xl font-bold text-green-900">
                    {Math.round((dashboard.percentual_concluido_auto + dashboard.percentual_concluido_gestor) / 2)}%
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Média geral</p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <Target className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Competências</p>
                  <p className="text-2xl font-bold text-orange-900">
                    {dashboard.todas_competencias_gaps?.length || 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Avaliadas</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Award className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Competências com Maiores Gaps */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <span>Competências com Maiores Gaps</span>
            </CardTitle>
            <CardDescription>
              Áreas onde há maior diferença entre autoavaliação e avaliação do gestor
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {dashboard.competencias_maior_gap?.length > 0 ? (
                dashboard.competencias_maior_gap.map((comp, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                          index === 0 ? 'bg-red-500' :
                          index === 1 ? 'bg-orange-500' :
                          index === 2 ? 'bg-yellow-500' :
                          'bg-gray-400'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{comp.competencia}</h4>
                          <p className="text-xs text-gray-600">
                            {comp.total_avaliacoes} avaliações
                          </p>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="flex items-center space-x-2">
                          {comp.gap_medio > 0 ? (
                            <TrendingUp className="h-5 w-5 text-blue-600" />
                          ) : (
                            <TrendingDown className="h-5 w-5 text-orange-600" />
                          )}
                          <span className={`text-xl font-bold ${
                            Math.abs(comp.gap_medio) > 1 ? 'text-red-600' :
                            Math.abs(comp.gap_medio) > 0.5 ? 'text-orange-600' :
                            'text-gray-600'
                          }`}>
                            {comp.gap_medio > 0 ? '+' : ''}{comp.gap_medio}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {comp.gap_medio > 0 ? 'Auto > Gestor' : 'Gestor > Auto'}
                        </p>
                      </div>
                    </div>

                    {/* Barra visual */}
                    <div className="relative h-8 bg-gray-100 rounded-full overflow-hidden">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-px h-full bg-gray-400"></div>
                      </div>
                      <div 
                        className={`absolute top-0 h-full ${
                          comp.gap_medio > 0 ? 'bg-blue-400' : 'bg-orange-400'
                        }`}
                        style={{
                          left: comp.gap_medio > 0 ? '50%' : `${50 + (comp.gap_medio * 10)}%`,
                          width: `${Math.abs(comp.gap_medio) * 10}%`
                        }}
                      ></div>
                    </div>

                    <div className="flex justify-between text-xs text-gray-600 mt-2">
                      <span>← Gestor avalia mais</span>
                      <span>Centro (alinhado)</span>
                      <span>Colaborador se avalia mais →</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 py-8">
                  Dados insuficientes para análise
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Heatmap de Competências */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <BarChart3 className="h-5 w-5 text-purple-600" />
              <span>Mapa de Calor - Todas as Competências</span>
            </CardTitle>
            <CardDescription>
              Visualização rápida do nível de alinhamento por competência
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {dashboard.todas_competencias_gaps?.map((comp, index) => {
                const absGap = Math.abs(comp.gap_medio);
                const color = 
                  absGap <= 0.5 ? 'bg-green-100 border-green-300 text-green-800' :
                  absGap <= 1.0 ? 'bg-yellow-100 border-yellow-300 text-yellow-800' :
                  'bg-red-100 border-red-300 text-red-800';
                
                return (
                  <div key={index} className={`p-3 border-2 rounded-lg ${color}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{comp.competencia}</p>
                        <p className="text-xs opacity-75 mt-1">
                          {comp.total_avaliacoes} avaliações
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold">
                          {comp.gap_medio > 0 ? '+' : ''}{comp.gap_medio}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Legenda */}
            <div className="flex items-center justify-center space-x-6 mt-6 pt-6 border-t">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-green-100 border-2 border-green-300 rounded"></div>
                <span className="text-sm text-gray-700">Alinhado (≤ 0.5)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-yellow-100 border-2 border-yellow-300 rounded"></div>
                <span className="text-sm text-gray-700">Moderado (0.5 - 1.0)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 bg-red-100 border-2 border-red-300 rounded"></div>
                <span className="text-sm text-gray-700">Desalinhado (&gt; 1.0)</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Ranking de Desalinhamento */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-green-700">
                Top 5 - Mais Alinhados
              </CardTitle>
              <CardDescription>
                Competências com melhor concordância
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {dashboard.todas_competencias_gaps
                  ?.sort((a, b) => Math.abs(a.gap_medio) - Math.abs(b.gap_medio))
                  .slice(0, 5)
                  .map((comp, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-green-50 rounded">
                      <div className="flex items-center space-x-2">
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium">{comp.competencia}</span>
                      </div>
                      <span className="text-sm font-bold text-green-700">
                        {Math.abs(comp.gap_medio).toFixed(2)}
                      </span>
                    </div>
                  )) || (
                  <p className="text-sm text-gray-500 text-center py-4">
                    Sem dados suficientes
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg text-red-700">
                Top 5 - Mais Desalinhados
              </CardTitle>
              <CardDescription>
                Competências que precisam de atenção
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {dashboard.todas_competencias_gaps
                  ?.sort((a, b) => Math.abs(b.gap_medio) - Math.abs(a.gap_medio))
                  .slice(0, 5)
                  .map((comp, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-red-50 rounded">
                      <div className="flex items-center space-x-2">
                        <AlertTriangle className="h-4 w-4 text-red-600" />
                        <span className="text-sm font-medium">{comp.competencia}</span>
                      </div>
                      <span className="text-sm font-bold text-red-700">
                        {Math.abs(comp.gap_medio).toFixed(2)}
                      </span>
                    </div>
                  )) || (
                  <p className="text-sm text-gray-500 text-center py-4">
                    Sem dados suficientes
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Insights e Recomendações */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Target className="h-5 w-5 text-blue-600" />
              <span>Insights e Recomendações</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dashboard.percentual_concluido_auto < 50 && (
                <div className="p-4 bg-orange-50 border-l-4 border-orange-400 rounded">
                  <p className="text-sm text-orange-900 font-medium">
                    ⚠️ Taxa de conclusão de autoavaliações abaixo de 50%
                  </p>
                  <p className="text-xs text-orange-700 mt-1">
                    Recomendação: Envie lembretes para os colaboradores pendentes.
                  </p>
                </div>
              )}

              {dashboard.percentual_concluido_gestor < 50 && (
                <div className="p-4 bg-orange-50 border-l-4 border-orange-400 rounded">
                  <p className="text-sm text-orange-900 font-medium">
                    ⚠️ Taxa de avaliação dos gestores abaixo de 50%
                  </p>
                  <p className="text-xs text-orange-700 mt-1">
                    Recomendação: Entre em contato com os gestores para incentivar a conclusão.
                  </p>
                </div>
              )}

              {dashboard.competencias_maior_gap?.[0]?.gap_medio && Math.abs(dashboard.competencias_maior_gap[0].gap_medio) > 1.5 && (
                <div className="p-4 bg-red-50 border-l-4 border-red-400 rounded">
                  <p className="text-sm text-red-900 font-medium">
                    🔴 Gap crítico identificado em "{dashboard.competencias_maior_gap[0].competencia}"
                  </p>
                  <p className="text-xs text-red-700 mt-1">
                    Recomendação: Agende reuniões 1:1 para alinhar expectativas nesta competência.
                  </p>
                </div>
              )}

              {dashboard.percentual_concluido_auto >= 90 && dashboard.percentual_concluido_gestor >= 90 && (
                <div className="p-4 bg-green-50 border-l-4 border-green-400 rounded">
                  <p className="text-sm text-green-900 font-medium">
                    ✅ Excelente engajamento! Ciclo quase completo.
                  </p>
                  <p className="text-xs text-green-700 mt-1">
                    Próximo passo: Revise os resultados e prepare os feedbacks individuais.
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard180;
